import { handleError } from '@slr/shared-library';
import { API_SOLUTION1_SERVICE_URL } from 'configs';
import { useCallback } from 'react';
import useGetApiConfig from '../useGetApiConfig';
import { PlatformAdminOfferPage } from './platformAdminOffer.types';

const buildQuery = (
  parameters: Record<string, string | number | boolean | undefined>
) => {
  const searchParams = new URLSearchParams();
  Object.entries(parameters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.set(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export const usePlatformAdminOffersApi = () => {
  const apiConfig = useGetApiConfig(API_SOLUTION1_SERVICE_URL);

  const authorizedFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const accessToken =
        typeof apiConfig.accessToken === 'function'
          ? await apiConfig.accessToken()
          : apiConfig.accessToken;
      const response = await fetch(`${apiConfig.basePath}${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(accessToken ? { Authorization: accessToken } : {}),
          ...init?.headers
        }
      });
      if (!response.ok) {
        let message = response.statusText;
        try {
          const errorBody = await response.json();
          message = errorBody?.message || errorBody?.detail || message;
        } catch {}
        const error = new Error(message) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }
      if (response.status === 204) {
        return undefined;
      }
      const responseText = await response.text();
      if (!responseText) {
        return undefined;
      }
      return JSON.parse(responseText);
    },
    [apiConfig]
  );

  const searchOffers = useCallback(
    (
      search: string | undefined,
      page: number,
      size: number,
      roomType?: string,
      visible?: boolean,
      availability?: string,
      sortBy?: string,
      sortDirection?: string
    ) =>
      authorizedFetch(
        `/admin/rooms/overview${buildQuery({
          search,
          page,
          size,
          roomType,
          visible,
          availability,
          sortBy,
          sortDirection
        })}`
      )
        .then((data) => data as PlatformAdminOfferPage)
        .catch(handleError),
    [authorizedFetch]
  );

  const suspendOffer = useCallback(
    (roomId: string, body: { reason: string; messageToProvider?: string }) =>
      authorizedFetch(`/admin/rooms/${encodeURIComponent(roomId)}/suspend`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).catch(handleError),
    [authorizedFetch]
  );

  const unsuspendOffer = useCallback(
    (roomId: string) =>
      authorizedFetch(`/admin/rooms/${encodeURIComponent(roomId)}/unsuspend`, {
        method: 'POST'
      }).catch(handleError),
    [authorizedFetch]
  );

  const removeOffer = useCallback(
    (roomId: string, body: { reason: string; messageToProvider?: string }) =>
      authorizedFetch(`/admin/rooms/${encodeURIComponent(roomId)}/remove`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).catch(handleError),
    [authorizedFetch]
  );

  return {
    searchOffers,
    suspendOffer,
    unsuspendOffer,
    removeOffer
  };
};
